/**
 * Push an event to the GTM dataLayer.
 * @param {string} event Event name.
 * @param {object} [params] Additional event parameters.
 */
export function pushDataLayer(event, params) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...params,
  });
}

/**
 * Generic click tracking.
 *
 * Any element carrying `data-tracking-event` is handled by the listener
 * below, dispatched to the matching function here via `CLICK_TRACKERS`.
 */

/**
 * Push the `click_cta_homepage` dataLayer event.
 * @param {string} label Label of the clicked CTA.
 * @param {string} url Destination url of the clicked CTA.
 */
export function trackClickCtaHomepage(label, url) {
  pushDataLayer('click_cta_homepage', {
    cta_label: label,
    ...(url && { cta_url: new URL(url, window.location.origin).pathname }),
  });
}

/**
 * Push the `click_inscription_offre` dataLayer event.
 * @param {string} offerName Name of the offer.
 * @param {string} url Url of the inscription link (mailto: or a form page).
 */
export function trackClickRegistrationOffer(offerName, url) {
  pushDataLayer('click_inscription_offre', {
    offre_name: offerName,
    inscription_type: url && url.startsWith('mailto:') ? 'mailto' : 'formulaire',
  });
}

/**
 * Push a `pagination_*` dataLayer event. Every pagination event shares the
 * same shape (`page_number` only), so one handler covers all of them.
 * @param {HTMLElement} trigger Clicked pagination link.
 */
function trackPagination(trigger) {
  pushDataLayer(trigger.dataset.trackingEvent, {
    page_number: Number(trigger.dataset.trackingLabel),
  });
}

/**
 * Push the `download_ressource` dataLayer event.
 * @param {string} name Name of the resource being downloaded.
 * @param {string} type Type of the resource being downloaded.
 * @param {string} url Url of the resource being downloaded.
 */
export function trackDownloadResource(name, type, url) {
  pushDataLayer('download_ressource', {
    ressource_name: name,
    ressource_type: type,
    ...(url && { ressource_url: new URL(url, window.location.origin).pathname }),
  });
}

/**
 * Push the `click_acces_membre_bloque` dataLayer event.
 * @param {string} name Name of the member-only resource.
 * @param {string} slug Url of the resource page.
 */
export function trackClickAccessMemberBlocked(name, slug) {
  pushDataLayer('click_acces_membre_bloque', {
    ressource_name: name,
    ...(slug && { ressource_slug: new URL(slug, window.location.origin).pathname }),
  });
}

/**
 * Push the `click_contact_dashboard` dataLayer event. No parameters.
 */
export function trackClickContactDashboard() {
  pushDataLayer('click_contact_dashboard');
}

/**
 * Push the `click_participe_concours` dataLayer event. No parameters.
 */
export function trackClickParticipateContest() {
  pushDataLayer('click_participe_concours');
}

/**
 * Push the `click_search_result` dataLayer event.
 * @param {string} searchQuery Search term that led to this result.
 * @param {string} position Position of the clicked result in the list (1-based).
 * @param {string} url Url of the clicked result.
 */
export function trackClickSearchResult(searchQuery, position, url) {
  pushDataLayer('click_search_result', {
    search_query: searchQuery,
    click_position: Number(position),
    ...(url && { click_url: new URL(url, window.location.origin).pathname }),
  });
}

/**
 * Push the `click_download_concours` dataLayer event.
 * @param {string} downloadType Type of concours document downloaded.
 */
export function trackClickDownloadContest(downloadType) {
  pushDataLayer('click_download_concours', {
    download_type: downloadType,
  });
}

/**
 * Map of trackable click events to the function that pushes their dataLayer event.
 */
const CLICK_TRACKERS = {
  click_cta_homepage: (trigger) =>
    trackClickCtaHomepage(trigger.dataset.trackingLabel, trigger.getAttribute('href')),
  click_inscription_offre: (trigger) =>
    trackClickRegistrationOffer(trigger.dataset.trackingLabel, trigger.getAttribute('href')),
  pagination_offre: trackPagination,
  pagination_ressource: trackPagination,
  download_ressource: (trigger) =>
    trackDownloadResource(
      trigger.dataset.trackingLabel,
      trigger.dataset.trackingResourceType,
      trigger.getAttribute('href'),
    ),
  click_acces_membre_bloque: (trigger) =>
    trackClickAccessMemberBlocked(
      trigger.dataset.trackingLabel,
      trigger.dataset.trackingResourceSlug,
    ),
  click_contact_dashboard: trackClickContactDashboard,
  click_participe_concours: trackClickParticipateContest,
  click_download_concours: (trigger) =>
    trackClickDownloadContest(trigger.dataset.trackingDownloadType),
  click_search_result: (trigger) =>
    trackClickSearchResult(
      trigger.dataset.trackingLabel,
      trigger.dataset.trackingPosition,
      trigger.getAttribute('href'),
    ),
};

/**
 * Push a dataLayer event for the tracked element that was clicked.
 * @param {MouseEvent} event
 */
function handleTrackedClick(event) {
  if (!(event.target instanceof Element)) {
    return;
  }

  const trigger = event.target.closest('[data-tracking-event]');
  if (!(trigger instanceof HTMLElement)) {
    return;
  }

  CLICK_TRACKERS[trigger.dataset.trackingEvent]?.(trigger);
}

/**
 * Start listening for clicks on any element carrying `data-tracking-event`.
 */
export function bindClickTracking() {
  document.addEventListener('click', handleTrackedClick);
}

/**
 * Generic filter tracking.
 *
 * Every `filter_*` event shares the same shape (`filter_type` + `filter_value`),
 * so it's pushed directly below instead of going through a dedicated function.
 */

/**
 * Push a dataLayer event for the tracked filter checkbox that changed.
 * @param {Event} event
 */
function handleTrackedFilterChange(event) {
  if (!(event.target instanceof HTMLInputElement) || !event.target.checked) {
    return;
  }

  const { trackingEvent, trackingFilterType, trackingFilterValue } = event.target.dataset;
  if (trackingEvent && trackingFilterType && trackingFilterValue) {
    pushDataLayer(trackingEvent, {
      filter_type: trackingFilterType,
      filter_value: trackingFilterValue.replaceAll('-', '_'),
    });
  }
}

/**
 * Start listening for changes on any element carrying `data-tracking-filter-type`.
 */
export function bindFilterTracking() {
  document.addEventListener('change', handleTrackedFilterChange);
}

/**
 * Generic form-start tracking.
 *
 * Fires once per form, on the first focus inside it. Forms are matched by
 * their DOM id (Gravity Forms always renders a stable `#gform_{id}`), since
 * some of them (e.g. the contact form) are inserted via a Gutenberg block
 * with no Twig template to carry a `data-tracking-event` attribute.
 */

const FORM_START_TRACKERS = {
  gform_4: 'form_start_contact',
};

const firedFormStarts = new Set();

/**
 * Push a `form_start_*` dataLayer event the first time a tracked form is focused.
 * @param {FocusEvent} event
 */
function handleFormFocusIn(event) {
  if (!(event.target instanceof Element)) {
    return;
  }

  const form = event.target.closest('form');
  if (!form || firedFormStarts.has(form.id)) {
    return;
  }

  const trackingEvent = FORM_START_TRACKERS[form.id];
  if (trackingEvent) {
    firedFormStarts.add(form.id);
    pushDataLayer(trackingEvent);
  }
}

/**
 * Start listening for the first focus inside any tracked form.
 */
export function bindFormStartTracking() {
  document.addEventListener('focusin', handleFormFocusIn);
}

/**
 * Push the `click_selector_homepage` dataLayer event.
 * @param {string} value Value of the selected option.
 */
export function trackClickSelectorHomepage(value) {
  pushDataLayer('click_selector_homepage', {
    selector_value: value,
  });
}

/**
 * Push the `click_add_favorite` dataLayer event.
 * @param {string} name Name of the offer added to favorites.
 * @param {string} url Url of the offer page.
 */
export function trackClickAddFavorite(name, url) {
  pushDataLayer('click_add_favorite', {
    offre_name: name,
    ...(url && { offre_slug: new URL(url, window.location.origin).pathname }),
  });
}

/**
 * Push a virtual `page_view` for the login panel's active tab. The panel
 * only ever renders logged-out (see login-panel.twig), so `user_logged` is
 * always 'non' here.
 * @param {string} pageType 'connexion' or 'inscription'.
 */
function trackLoginPanelPageView(pageType) {
  pushDataLayer('page_view', {
    page_type: pageType,
    user_logged: 'non',
  });
}

/**
 * Track virtual page views for the login/registration panel (#login-panel),
 * since it's a slide-in panel, not a real page load — no server-rendered
 * page_view applies here.
 *
 * Two `@studiometa/ui` component events matter, and neither is sufficient
 * alone: `Tabs`' `enable` fires whenever a tab becomes active, but ALSO
 * fires once silently at page load (to activate the first tab), before the
 * panel is even visible. `Panel`'s (a `Modal` subclass) `open` only fires
 * when the user actually opens it, but doesn't say which tab is showing —
 * and it isn't always "connexion": the tabs don't reset when the panel
 * closes, so a user who last viewed "inscription" and reopens the panel
 * lands back on "inscription". So: track the active tab as it changes via
 * `enable` (pushing immediately only if the panel is already open — a live
 * tab switch), and push again on `open`, using whichever tab is active at
 * that moment.
 */
export function bindLoginPanelTracking() {
  const tabs = document.querySelector('#login-tabs');
  const panel = document.querySelector('#login-panel');
  if (!tabs || !panel) {
    return;
  }

  let activeTab = 'connexion';
  let isPanelOpen = false;

  tabs.addEventListener('enable', (/** @type {CustomEvent} */ event) => {
    const [item] = event.detail;
    const tab = item.btn.dataset.trackingLoginTab;

    if (!tab) {
      return;
    }

    activeTab = tab;

    if (isPanelOpen) {
      trackLoginPanelPageView(activeTab);
    }
  });

  panel.addEventListener('open', () => {
    isPanelOpen = true;
    trackLoginPanelPageView(activeTab);
  });

  panel.addEventListener('close', () => {
    isPanelOpen = false;
  });
}
